import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, LayoutDashboard, Building2, CreditCard, ScrollText, Shield, ArrowLeft, RefreshCw, Tag, Activity, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SuperAdminLayout = ({ children, auth, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/superadmin/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/superadmin/tenants', label: 'Entreprises', icon: Building2 },
    { path: '/superadmin/plans', label: 'Plans tarifaires', icon: Tag },
    { path: '/superadmin/payments', label: 'Paiements', icon: CreditCard },
    { path: '/superadmin/logs', label: 'Journal', icon: ScrollText },
    { path: '/superadmin/health', label: 'Sante systeme', icon: Activity },
    { path: '/superadmin/communications', label: 'Communications', icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col bg-slate-900 border-r border-slate-800">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shrink-0">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white">Vortekio</span>
          </div>
          <p className="text-xs text-indigo-400 mt-1">Super-Administration SaaS</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Button key={item.path} onClick={() => navigate(item.path)} variant="ghost" className={`w-full justify-start text-sm text-slate-300 hover:bg-slate-800 hover:text-white ${isActive ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : ''}`} data-testid={`sa-nav-${item.path.split('/').pop()}`}>
                <Icon className="mr-3 w-4 h-4" />
                {item.label}
              </Button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="mb-3">
            <p className="text-sm font-medium text-white">{auth?.admin?.name}</p>
            <p className="text-xs text-slate-400">{auth?.admin?.email}</p>
          </div>
          <Button onClick={onLogout} variant="outline" className="w-full justify-start text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-white" data-testid="superadmin-logout-button">
            <LogOut className="mr-2 w-4 h-4" /> Déconnexion
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <div className="md:hidden bg-slate-900 border-b border-slate-800 sticky top-0 z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
                <Shield className="w-3 h-3 text-white" />
              </div>
              <span className="text-base font-bold text-white">Vortekio SaaS</span>
            </div>
            <Button variant="outline" size="sm" onClick={onLogout} className="text-slate-300 border-slate-700">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <div className="px-4 pb-3 flex space-x-2 overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button key={item.path} onClick={() => navigate(item.path)} size="sm" variant={isActive ? 'default' : 'outline'} className={`whitespace-nowrap text-xs ${isActive ? 'bg-indigo-600' : 'text-slate-300 border-slate-700'}`}>
                  <Icon className="mr-1 w-3 h-3" /> {item.label}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {/* Top toolbar - Back & Refresh */}
          <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => {
                if (window.history.length > 2) navigate(-1);
                else navigate('/superadmin/dashboard');
              }}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-800"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Retour</span>
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-800"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
